/**
 * FilterBar configuration builder for the Buildings listing.
 *
 * Produces a FilterConfig[] array that represents every current filter from the
 * parity matrix, respecting hide rules and locale labels (IP-15527).
 *
 * Hide rules:
 *  - account: hidden when scope is account (embed); `locked` is not set — user can still
 *    toggle other filters. Account is simply absent in that context.
 *  - territory: hidden when hasGlobalTerritoriesApplied() returns true.
 *  - additionalTerritory: hidden until hasPrimaryTerritoryScope (= globalTerritoriesApplied
 *    OR territory filter has at least one value). Auto-clear on scope drop is handled by the
 *    parent component via useEffect (not this config function).
 *  - compliancePlatform: only included when compliancePlatformsForSelect has entries.
 *
 * Labels (IP-15527): every label uses the legacy locale string recorded in the parity matrix.
 *
 * Unassigned sentinel:
 *  - account: value 0 (IP-15547, same convention as territories)
 *  - territory: value 0 (maps to territory_id = nil in the datatable)
 *
 * Default active filter keys:
 *  - standalone: account, status, tags, name, externalId, territory, additionalTerritory, salesPerson
 *  - account embed: status, tags, name, externalId, territory, additionalTerritory, salesPerson
 *    (account is omitted — it's scope-injected and the filter is hidden anyway)
 *
 * salesPerson is always locked (active=true, locked=true) — per IP-15509, toggling it off
 * in Manage filters makes it unreachable. `locked` must NOT disable the chip (IP-15825).
 *
 * Do NOT render FilterBar here — this task produces only the config and hide-rule logic.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { hasGlobalTerritoriesApplied } from '@helpers/territories';
import { withUnassignedFirstPage, withUnassignedSelected } from '@helpers/unassignedLookup';
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function isActive(activeFilterKeys, field) {
    return activeFilterKeys.includes(field);
}
function isFilterValuePopulated(value) {
    if (Array.isArray(value))
        return value.length > 0;
    if (typeof value === 'string')
        return value !== '';
    if (typeof value === 'boolean')
        return true;
    return false;
}
// ---------------------------------------------------------------------------
// Hook (pure computation — no React state)
// ---------------------------------------------------------------------------
export function useBuildingsFilterConfig(opts) {
    const { scope, filters, activeFilterKeys, onChange, onToggle, loadAccounts, loadTerritories, loadAdditionalTerritories, loadTags, loadSalesPersons, loadSelectedAccounts, loadSelectedTerritories, loadSelectedAdditionalTerritories, loadSelectedTags, loadSelectedSalesPersons, compliancePlatformsForSelect } = opts;
    const isAccountEmbed = scope.type === 'account';
    const globalTerritoriesApplied = hasGlobalTerritoriesApplied();
    const hasPrimaryTerritoryScope = globalTerritoriesApplied || isFilterValuePopulated(filters.territory);
    // Helper to update a single filter field
    const set = (field, value) => {
        onChange(Object.assign(Object.assign({}, filters), { [field]: value }));
    };
    const filterList = [];
    // --- account (paged multi-select, hidden when accountId is present = account embed) ---
    // IP-15547: Unassigned sentinel (value 0) → account_id = nil
    if (!isAccountEmbed) {
        filterList.push({
            type: 'pagedMultiSelect',
            field: 'account',
            label: 'Account',
            active: isActive(activeFilterKeys, 'account'),
            value: filters.account,
            loadOptions: (query, cursor) => __awaiter(this, void 0, void 0, function* () {
                return withUnassignedFirstPage(yield loadAccounts(query, cursor), query, cursor);
            }),
            loadSelectedValues: withUnassignedSelected(loadSelectedAccounts),
            onChange: (v) => set('account', v)
        });
    }
    // --- status (boolean select, Active = true / Inactive = false) ---
    filterList.push({
        type: 'boolean',
        field: 'status',
        label: 'Status',
        active: isActive(activeFilterKeys, 'status'),
        value: filters.status,
        trueLabel: 'Active',
        falseLabel: 'Inactive',
        onChange: (v) => set('status', v)
    });
    // --- tags (paged multi-select) ---
    filterList.push({
        type: 'pagedMultiSelect',
        field: 'tags',
        label: 'Building Tags',
        active: isActive(activeFilterKeys, 'tags'),
        value: filters.tags,
        loadOptions: loadTags,
        loadSelectedValues: loadSelectedTags,
        onChange: (v) => set('tags', v)
    });
    // --- name (text contains) ---
    filterList.push({
        type: 'text',
        field: 'name',
        label: 'Name',
        active: isActive(activeFilterKeys, 'name'),
        value: filters.name,
        onChange: (v) => set('name', v)
    });
    // --- externalId (text contains, chip active by default per parity matrix 2026-09-07) ---
    filterList.push({
        type: 'text',
        field: 'externalId',
        label: 'External ID',
        active: isActive(activeFilterKeys, 'externalId'),
        value: filters.externalId,
        onChange: (v) => set('externalId', v)
    });
    // --- territory (paged multi-select, hidden when global territories apply, Unassigned sentinel) ---
    if (!globalTerritoriesApplied) {
        filterList.push({
            type: 'pagedMultiSelect',
            field: 'territory',
            label: 'Territory',
            active: isActive(activeFilterKeys, 'territory'),
            value: filters.territory,
            loadOptions: (query, cursor) => __awaiter(this, void 0, void 0, function* () {
                return withUnassignedFirstPage(yield loadTerritories(query, cursor), query, cursor);
            }),
            loadSelectedValues: withUnassignedSelected(loadSelectedTerritories),
            onChange: (v) => set('territory', v)
        });
    }
    // --- additionalTerritory (paged multi-select, hidden until hasPrimaryTerritoryScope) ---
    // When primary scope drops, auto-clear is the parent component's responsibility (useEffect).
    if (hasPrimaryTerritoryScope) {
        filterList.push({
            type: 'pagedMultiSelect',
            field: 'additionalTerritory',
            label: 'Territory (refine)',
            active: isActive(activeFilterKeys, 'additionalTerritory'),
            value: filters.additionalTerritory,
            loadOptions: loadAdditionalTerritories,
            loadSelectedValues: loadSelectedAdditionalTerritories,
            onChange: (v) => set('additionalTerritory', v)
        });
    }
    // --- salesPerson (paged multi-select, always active; locked in Manage filters only — IP-15509 / IP-15825) ---
    filterList.push({
        type: 'pagedMultiSelect',
        field: 'salesPerson',
        label: 'Sales Person',
        active: true,
        locked: true,
        value: filters.salesPerson,
        loadOptions: loadSalesPersons,
        loadSelectedValues: loadSelectedSalesPersons,
        onChange: (v) => set('salesPerson', v)
    });
    // --- compliancePlatform (multi-select, only when facade options exist) ---
    if (compliancePlatformsForSelect.length > 0) {
        filterList.push({
            type: 'multiSelect',
            field: 'compliancePlatform',
            label: 'Compliance Platform',
            active: isActive(activeFilterKeys, 'compliancePlatform'),
            value: filters.compliancePlatform,
            options: compliancePlatformsForSelect.map((o) => ({ value: o.value, label: o.label })),
            onChange: (v) => set('compliancePlatform', v)
        });
    }
    // Wire onToggle (unused for locked filters; still wired for consistency with FilterBar API)
    filterList.forEach((f) => {
        if (!('onToggle' in f)) {
            f.onToggle = () => onToggle(f.field);
        }
    });
    // ── Default active filter keys for the current scope ──────────────────────
    // Standalone: account, status, tags, name, externalId, territory, additionalTerritory, salesPerson
    // Account embed: omit account (hidden filter; injected from scope)
    const standaloneDefaults = [
        'account',
        'status',
        'tags',
        'name',
        'externalId',
        'territory',
        'additionalTerritory',
        'salesPerson'
    ];
    const defaultActiveFilterKeys = isAccountEmbed
        ? standaloneDefaults.filter((k) => k !== 'account')
        : standaloneDefaults;
    return { filters: filterList, defaultActiveFilterKeys };
}
